import { useState, useEffect } from 'react';
import { API_BASE } from '../../utils/config';

const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'BU_HEAD',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

let reqsCache = null;
let reqsCacheTimestamp = 0;
const REQS_CACHE_TTL_MS = 15000;

export default function BURequisitionApprovals({ onAction }) {
  const [requisitions, setRequisitions] = useState(reqsCache || []);
  const [loading, setLoading] = useState(!reqsCache);
  const [submittingId, setSubmittingId] = useState(null);

  const fetchRequisitions = async (force = false) => {
    const now = Date.now();
    if (!force && reqsCache && (now - reqsCacheTimestamp < REQS_CACHE_TTL_MS)) {
      setRequisitions(reqsCache);
      setLoading(false);
      return;
    }

    try {
      if (!reqsCache) setLoading(true);
      const data = await apiCall('/api/requisitions/global');
      reqsCache = data;
      reqsCacheTimestamp = Date.now();
      setRequisitions(data);
    } catch (err) {
      console.error('Failed to fetch requisitions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const handleApprove = async (id) => {
    try {
      setSubmittingId(id);
      await apiCall(`/api/requisitions/${id}/approve`, {
        method: 'POST'
      });
      reqsCache = null;
      fetchRequisitions(true);
      if (onAction) onAction();
    } catch (err) {
      console.error('Failed to approve requisition:', err);
      alert('Failed to approve requisition.');
    } finally {
      setSubmittingId(null);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'DRAFT': return styles.badgeDraft;
      case 'BU_APPROVED': return styles.badgeApproved;
      case 'PUBLISHED': return styles.badgePublished;
      case 'FILLED': return styles.badgeFilled;
      default: return styles.badgeDraft;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.cardHeader}>
        <h2 style={styles.title}>Hiring Requisition Approvals</h2>
        <p style={styles.subtitle}>Review new position demands submitted by Project Managers and approve budgets.</p>
      </div>

      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.loader}>Loading requisitions...</div>
        ) : requisitions.length === 0 ? (
          <div style={styles.empty}>No hiring requisitions requested.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.trHeader}>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Job Title</th>
                  <th style={styles.th}>Skillset</th>
                  <th style={styles.th}>Experience</th>
                  <th style={styles.th}>Budget</th>
                  <th style={styles.th}>HR Name</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th} style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requisitions.map(req => (
                  <tr key={req.id} style={styles.tr}>
                    <td style={styles.tdProject}>{req.project_name}</td>
                    <td style={styles.tdTitle}>{req.title}</td>
                    <td style={styles.td}>{req.skillset}</td>
                    <td style={styles.td}>{req.exp_required}</td>
                    <td style={styles.td}>₹{req.budget} LPA</td>
                    <td style={styles.td}>{req.hr_name || 'Not Assigned'}</td>
                    <td style={styles.td}>
                      <span style={getStatusBadgeStyle(req.status)}>{req.status}</span>
                    </td>
                    <td style={styles.tdAction}>
                      {req.status === 'DRAFT' ? (
                        <button 
                          style={styles.btnApprove} 
                          onClick={() => handleApprove(req.id)}
                          disabled={submittingId === req.id}
                        >
                          {submittingId === req.id ? 'Approving...' : 'Approve'}
                        </button>
                      ) : (
                        <span style={styles.actionTextDone}>Approved</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  cardHeader: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700'
  },
  subtitle: {
    margin: '8px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  loader: {
    textAlign: 'center',
    padding: '24px',
    color: 'var(--text-muted, #64748b)'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdProject: {
    padding: '16px',
    fontWeight: '600',
    color: 'var(--accent-primary, #4f46e5)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  tdAction: {
    padding: '16px',
    textAlign: 'right'
  },
  btnApprove: {
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.2)'
  },
  actionTextDone: {
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px',
    fontWeight: '500'
  },
  badgeDraft: {
    backgroundColor: '#cbd5e1',
    color: '#334155',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeApproved: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgePublished: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeFilled: {
    backgroundColor: '#f3e8ff',
    color: '#6b21a8',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  }
};
